import { type FC, useCallback, useContext, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { useQuery } from "@apollo/client";
import { SearchableCard } from "@snapchat/snap-design-system";

import { useFeatureGates } from "../../../../cof-feature-gates/hooks";
import { type PaginationFilter } from "../../../../core/pagination/models";
import { type AbsoluteRoute, type RouteParams } from "../../../../core/routes";
import { useQueryParams } from "../../../../core/routes/hooks";
import { CofConfigId, type MyLensesLens, type SurfaceType } from "../../../../gql-generated/types";
import {
    ListContainer,
    PageContent,
    PageContentSpinner,
    PageHeader,
    PageSubHeader,
    SearchContainer,
} from "../../../../layout/components";
import { LensDistributionRestrictionsModal } from "../../../../lens/components/shared";
import { useLenses } from "../../../../lenses/hooks";
import { type LensModalProps } from "../../../../lenses/models";
import { UserInfoContext } from "../../../../shared/api/contexts";
import { VirtualizedList } from "../../../../shared/components";
import {
    LensFoldersSearchParams,
    SearchParamKeys,
    useSearchParams,
    useSelectedLensFolder,
    useSelectedLensFolderV3,
    useSelectedOrganizationContainer,
    useToggleState,
} from "../../../../shared/hooks";
import { type KeyedOption } from "../../../../shared/models";
import { PaginationAtomsKeys } from "../../../../shared/state";
import {
    type GetAllPreSubmissionInfoWithOrgResourcesQuery,
    type GetAllPreSubmissionInfoWithOrgResourcesQueryVariables,
    getAllPreSubmissionInfoWithOrgResources,
} from "../../../api/queries";
import { getPreSubmissionInfoQuery } from "../../../api/queries/getPreSubmissionInfo";
import {
    type GetPreSubmissionInfoQuery,
    type GetPreSubmissionInfoQueryVariables,
} from "../../../api/queries/getPreSubmissionInfo.gql-generated";
import {
    useLensSpaceOptions,
    useLensSpaceOptionsV2,
    useOrganizationOptions,
    useOrganizationOptionsV2,
} from "../../../hooks";
import { SelectLensSpaceDropdownInputSection, SelectOrganizationDropdownInputSection } from "../../lens-space";
import { ListSizeConfig, LoadableLensRow } from "../shared";

import {
    lensSpaceLensList,
    lensSpaceLensesForUpdatePageContent,
    lensSpaceSelectionContainer,
} from "./LensSpaceLensesForUpdate.styles";
import { SelectLensToUpdateHeader, SelectLensToUpdateSubHeader } from "./messages.generated";

interface LensFoldersLensList {
    selectedLensFolderId: string;
    surfaceTypeList: readonly SurfaceType[];
}

const LensFoldersLensList: FC<LensFoldersLensList> = ({ selectedLensFolderId, surfaceTypeList }) => {
    const filterByOptions: Partial<PaginationFilter> = useMemo(
        () => ({
            locked: false,
            surfaceTypeList,
        }),
        [surfaceTypeList],
    );
    const { lenses, isFirstFetching, loadMore, hasMore, isReFetching, lensSearchText, setLensSearchText } = useLenses({
        filterByOptions,
        lensSpaceId: selectedLensFolderId,
        paginationAtomsKey: PaginationAtomsKeys.LensFolderSubmission,
        forUpdate: true,
    });

    const [distributionRestrictionsModalProps, showDistributionRestrictionsModal] = useToggleState();
    const [modalLens, setModalLens] = useState<MyLensesLens | null>(null);

    const distributionRestrictionsProps: LensModalProps = {
        isEnabled: true,
        showModal: showDistributionRestrictionsModal,
        setLens: setModalLens,
    };

    if (isFirstFetching) {
        return <PageContentSpinner height="calc(50vh - 56px)" />;
    }

    return (
        <>
            <ListContainer className={lensSpaceLensList}>
                <SearchableCard
                    searchPlaceholder="Search Lenses"
                    padBody={false}
                    onSearch={setLensSearchText}
                    searchValue={lensSearchText}
                >
                    <SearchContainer
                        loading={isReFetching}
                        noResults={lenses.length === 0}
                        height={ListSizeConfig.height}
                    >
                        <VirtualizedList
                            data={{
                                list: lenses,
                                data: {
                                    distributionRestrictionsProps,
                                },
                            }}
                            renderMore={loadMore}
                            hasMore={hasMore}
                            itemSize={ListSizeConfig.lensHeight}
                            height={ListSizeConfig.height}
                            width="100%"
                        >
                            {LoadableLensRow}
                        </VirtualizedList>
                    </SearchContainer>
                </SearchableCard>
            </ListContainer>
            {modalLens && (
                <LensDistributionRestrictionsModal
                    modalProps={distributionRestrictionsModalProps}
                    lensId={modalLens.id}
                    lensName={modalLens.name}
                />
            )}
        </>
    );
};

interface LensSpaceLensesForUpdate {
    organizationOptions: KeyedOption[];
    lensSpaceOptions: KeyedOption[];
    surfaceTypeList: readonly SurfaceType[];
    lensFoldersLoading?: boolean;
}

export const LensSpaceLensesForUpdate: FC<LensSpaceLensesForUpdate> = ({
    lensSpaceOptions,
    surfaceTypeList,
    organizationOptions,
    lensFoldersLoading,
}) => {
    const selectedLensFolderV3 = useSelectedLensFolderV3();
    const { orgId: selectedOrganizationId } = useSelectedOrganizationContainer();
    const { lensFolder: selectedLensFolder } = useSelectedLensFolder();

    const { configs } = useFeatureGates();
    const isMyLensesOrgsV2Enabled = configs[CofConfigId.MyLensesOrgsV2].enabled;

    const lensFolderId = isMyLensesOrgsV2Enabled ? selectedLensFolder?.id : selectedLensFolderV3?.id;

    const { updateQueryParams } = useQueryParams();

    const onSelectLensFolder = useCallback(
        (lensFolderId: string) => {
            updateQueryParams({ [LensFoldersSearchParams.LensFolder]: lensFolderId });
        },
        [updateQueryParams],
    );

    const onSelectedOrganization = useCallback(
        (selectedOrgId: string) => {
            updateQueryParams({
                [SearchParamKeys.Organization]: selectedOrgId,
                [LensFoldersSearchParams.LensFolder]: "",
            });
        },
        [updateQueryParams],
    );

    const selectedOrganizationOption = useMemo(() => {
        return organizationOptions.find((option) => option.value === selectedOrganizationId);
    }, [selectedOrganizationId, organizationOptions]);

    const selectedLensFolderOption = useMemo(() => {
        return lensSpaceOptions.find((option) => option.value === lensFolderId);
    }, [lensFolderId, lensSpaceOptions]);

    return (
        <PageContent className={lensSpaceLensesForUpdatePageContent}>
            <PageHeader>
                <SelectLensToUpdateHeader />
            </PageHeader>
            <PageSubHeader>
                <SelectLensToUpdateSubHeader />
            </PageSubHeader>
            <div className={lensSpaceSelectionContainer}>
                <SelectOrganizationDropdownInputSection
                    onChange={onSelectedOrganization}
                    options={organizationOptions}
                    defaultOption={selectedOrganizationOption}
                />
                <SelectLensSpaceDropdownInputSection
                    isLoading={lensFoldersLoading}
                    options={lensSpaceOptions}
                    onChange={onSelectLensFolder}
                    value={selectedLensFolderOption}
                />
            </div>
            {lensFolderId && (
                <LensFoldersLensList selectedLensFolderId={lensFolderId} surfaceTypeList={surfaceTypeList} />
            )}
        </PageContent>
    );
};

export const LensSpaceLensesForUpdateContainer: FC = () => {
    const { submissionId = "" } = useParams<RouteParams[AbsoluteRoute.SUBMIT_LENS_SELECT]>();
    const searchParams = useSearchParams();
    const organizationId = searchParams.get(SearchParamKeys.Organization) ?? undefined;
    const { configs } = useFeatureGates();
    const isMyLensesOrgsV2Enabled = configs[CofConfigId.MyLensesOrgsV2].enabled;

    const {
        loading: loadingV1,
        data: dataV1,
        error: errorV1,
    } = useQuery<GetAllPreSubmissionInfoWithOrgResourcesQuery, GetAllPreSubmissionInfoWithOrgResourcesQueryVariables>(
        getAllPreSubmissionInfoWithOrgResources,
        {
            variables: { submissionId },
            skip: isMyLensesOrgsV2Enabled,
        },
    );

    const {
        loading: loadingV2,
        data: dataV2,
        error: errorV2,
    } = useQuery<GetPreSubmissionInfoQuery, GetPreSubmissionInfoQueryVariables>(getPreSubmissionInfoQuery, {
        variables: { submissionId },
        skip: !isMyLensesOrgsV2Enabled,
    });

    const loading = isMyLensesOrgsV2Enabled ? loadingV2 : loadingV1;
    const data = isMyLensesOrgsV2Enabled ? dataV2 : dataV1;
    const error = isMyLensesOrgsV2Enabled ? errorV2 : errorV1;

    const userInfo = useContext(UserInfoContext);

    const surfaceTypeList = data?.getPreSubmissionInfo.surfaceType ?? [];

    const organizationOptionsV1 = useOrganizationOptions(
        dataV1,
        userInfo?.data?.getUserProfile.defaultOrganizationId ?? "",
    );

    const organizationOptionsV2 = useOrganizationOptionsV2();

    const organizationOptions = isMyLensesOrgsV2Enabled ? organizationOptionsV2 : organizationOptionsV1;

    const lensSpaceOptionsV1 = useLensSpaceOptions(dataV1, organizationId ?? "");
    const {
        options: lensSpaceOptionsV2,
        loading: lensFoldersLoading,
        error: lensFoldersError,
    } = useLensSpaceOptionsV2();

    const lensSpaceOptions = isMyLensesOrgsV2Enabled ? lensSpaceOptionsV2 : lensSpaceOptionsV1;

    if (loading || !data || userInfo?.loading) {
        return <PageContentSpinner />;
    }

    if (error) {
        throw error;
    }
    if (lensFoldersError) {
        throw lensFoldersError;
    }

    return (
        <>
            <LensSpaceLensesForUpdate
                organizationOptions={organizationOptions}
                lensSpaceOptions={lensSpaceOptions}
                surfaceTypeList={surfaceTypeList}
                lensFoldersLoading={lensFoldersLoading}
            />
        </>
    );
};
